import { useLazyQuery, useQuery } from '@apollo/client/react';
import { useSetAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';

import {
  GetCurrentPractitionerDocument,
  GetTracingReportDispensingRequestDocument,
  GetTracingReportDraftByCurrentPractitionerDocument,
  GetTracingReportSheetDocument,
} from '~/graphql/generated/graphql';
import { getMe, getNode } from '~/graphql/utility';
import { tracingReportPaneState } from '~/state/partials/tracingreport_panel/atoms';
import { Label } from '~/utils/label';

import { buildText } from '../../sheet';
import { Fields } from './types';

export const useDefaultValues = (
  selectedSheetId: string | null,
  patientId: string | null,
  guestPatientId: string | null,
) => {
  const { data } = useQuery(GetCurrentPractitionerDocument);
  const [getSheet, { data: sheetData }] = useLazyQuery(GetTracingReportSheetDocument);
  const [getDispensingRequest, { data: requestData }] = useLazyQuery(
    GetTracingReportDispensingRequestDocument,
  );

  const currentPractitioner = data ? getMe(data) : undefined;
  const sheet = getNode(sheetData, 'MedicationFollowupQuestionnaireSheet');
  const answerdAt = sheet ? Label.YYYYMMDDja(sheet.createdAt) : undefined;
  const sheetForReport = answerdAt && sheet ? buildText(answerdAt, sheet.answers) : undefined;
  const appointment = getNode(requestData, 'Patient')?.appointments.nodes[0];
  const medicalInstitution = appointment?.pfDispensingRequests[0]?.medicalInstitution;

  const setTracingReportPaneState = useSetAtom(tracingReportPaneState);
  const [isLoadedTracingReportDraft, setIsLoadedTracingReportDraft] = useState(false);
  const [
    getTracingReportDraftByCurrentPractitioner,
    { loading: loadingTracingReportDraft, data: tracingReportDraftData },
  ] = useLazyQuery(GetTracingReportDraftByCurrentPractitionerDocument, {
    fetchPolicy: 'network-only',
  });
  const tracingReportDraft = useMemo(() => {
    const node =
      getNode(tracingReportDraftData, 'Patient') ||
      getNode(tracingReportDraftData, 'WebVisitor') ||
      getNode(tracingReportDraftData, 'DirectVisitor');

    return node?.medixsPatient?.tracingReportDraft || node?.tracingReportDrafts.nodes[0] || null;
  }, [tracingReportDraftData]);

  useEffect(() => {
    if (!tracingReportDraftData) return;

    const node =
      getNode(tracingReportDraftData, 'Patient') ||
      getNode(tracingReportDraftData, 'WebVisitor') ||
      getNode(tracingReportDraftData, 'DirectVisitor');

    const tracingReportDraftId =
      node?.medixsPatient?.tracingReportDraft?.id || node?.tracingReportDrafts.nodes[0]?.id || null;
    setIsLoadedTracingReportDraft(true);
    setTracingReportPaneState((state) => ({
      ...state,
      tracingReportDraftId,
    }));
  }, [tracingReportDraftData, setTracingReportPaneState]);

  const initialValues: Fields = {
    clinicName: '',
    clinicalDepartment: '',
    doctorName: '',
    prescriptionIssueDate: new Date(),
    dispensingDate: new Date(),
    pharmacistName: '',
    prescriptionDrugs: [''],
    report: '',
    suggestion: '',
  };

  const defaultValuesWithoutDraft: Fields = useMemo(() => {
    if (currentPractitioner) {
      initialValues.pharmacistName = `${currentPractitioner.familyName}${currentPractitioner.givenName}`;
    }

    if (sheetForReport) {
      initialValues.report = sheetForReport;
    }

    if (medicalInstitution) {
      initialValues.clinicName = medicalInstitution.medicalInstitution.name || '';
      initialValues.clinicalDepartment = medicalInstitution.consultation.departmentName || '';
      initialValues.prescriptionIssueDate = new Date(medicalInstitution.prescription.issuedDate);
    }

    return initialValues;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPractitioner, sheetForReport, medicalInstitution]);

  const defaultValues: Fields = useMemo(() => {
    if (isLoadedTracingReportDraft && tracingReportDraft) {
      const data = tracingReportDraft.data;
      const prescriptionDrugs = data.prescriptionDrugs?.map((value) => {
        if (value.drugName) {
          return value.drugName;
        } else {
          return '';
        }
      });
      return {
        clinicName: data.clinicName || '',
        clinicalDepartment: data.clinicalDepartment || '',
        doctorName: data.doctorName || '',
        prescriptionIssueDate: data.prescriptionIssueDate
          ? new Date(data.prescriptionIssueDate)
          : null,
        dispensingDate: data.dispensingDate ? new Date(data.dispensingDate) : null,
        pharmacistName: data.pharmacistName || '',
        prescriptionDrugs: prescriptionDrugs || [],
        report: data.report || '',
        suggestion: data.suggestion || '',
      };
    }
    if (currentPractitioner) {
      initialValues.pharmacistName = `${currentPractitioner.familyName}${currentPractitioner.givenName}`;
    }

    if (sheetForReport) {
      initialValues.report = sheetForReport;
    }

    if (medicalInstitution) {
      initialValues.clinicName = medicalInstitution.medicalInstitution.name || '';
      initialValues.clinicalDepartment = medicalInstitution.consultation.departmentName || '';
      initialValues.prescriptionIssueDate = new Date(medicalInstitution.prescription.issuedDate);
    }

    return initialValues;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPractitioner, sheetForReport, medicalInstitution, isLoadedTracingReportDraft]);

  useEffect(() => {
    if (selectedSheetId) {
      getSheet({
        variables: {
          sheetId: selectedSheetId,
        },
      });
    }
  }, [getSheet, selectedSheetId]);

  useEffect(() => {
    if (patientId) {
      getDispensingRequest({
        variables: {
          patientId,
        },
      });
    }
  }, [getDispensingRequest, patientId]);

  useEffect(() => {
    if (patientId) {
      getTracingReportDraftByCurrentPractitioner({
        variables: {
          id: patientId,
        },
      });
    }
    if (guestPatientId) {
      getTracingReportDraftByCurrentPractitioner({
        variables: {
          id: guestPatientId,
        },
      });
    }
  }, [getTracingReportDraftByCurrentPractitioner, patientId, guestPatientId]);

  return {
    defaultValuesWithoutDraft,
    defaultValues,
    tracingReportDraft,
    loadingTracingReportDraft,
  };
};
